import React, { createContext, useContext, useEffect, useState } from 'react';
import { EnrichedUser, useAuth } from './AuthProvider.tsx';
import { PageLoading } from '@cloudflare-monorepo/ui/components/PageLoading.tsx';
import { useLogger } from '@cloudflare-monorepo/ui/providers/LoggerProvider.tsx';
import { Logger } from '@cloudflare-monorepo/utils/logging/logger';
import { TableState } from '@cloudflare-monorepo/ui/components/GenericTable.tsx';
export type { TableState };

const LATEST_SCHEMA = 1;

export type TourState = {
  done: boolean;
};

export type LocalEntry<T> = T & {
  schema: typeof LATEST_SCHEMA;
};

export const serializeTableState = (state: TableState): string => {
  return `${state.currentPage || 0}|${state.pageSize || 0}|${state.sortColumn || ''}|${state.sortDirection || ''}|${JSON.stringify(state.filters || {})}`;
};

export class UserStorageService {
  public constructor(
    private user: EnrichedUser,
    private logger: Logger,
  ) {}

  public get<T>(entityKey: string): T | null {
    const key = this.user.email + '/' + entityKey;
    const entry = localStorage?.getItem(key);
    if (!entry) {
      return null;
    }
    const decoded: LocalEntry<T> = JSON.parse(entry);
    if (decoded.schema !== LATEST_SCHEMA) {
      this.logger.warn('dropping obsolete state from storage', decoded);
      return null;
    }
    return decoded;
  }

  public save<T>(entityKey: string, state: T): void {
    const storageRecord: LocalEntry<T> = {
      schema: LATEST_SCHEMA,
      ...state,
    };
    const serialized = JSON.stringify(storageRecord);

    const key = this.user.email + '/' + entityKey;

    localStorage?.setItem(key, serialized);
  }

  public getTableState(table: string): TableState | null {
    return this.get<TableState>('table/' + table);
  }

  public saveTableState(table: string, state: TableState): void {
    return this.save<TableState>('table/' + table, state);
  }
}

// Define the shape of our context
interface UserStorageContextType {
  service: UserStorageService | null;
}

// Create the context
const UserStorageContext = createContext<UserStorageContextType | undefined>(undefined);

// Provider component
export const UserStorageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const logger = useLogger('user-storage');

  logger.debug('UserStorageProvider rendering');

  const { user, isAuthenticated, isLoading } = useAuth();
  const [userStorage, setUserStorage] = useState<UserStorageService | null>(null);

  if (!isAuthenticated) {
    logger.error('this provider should never render when isAuthenticated is false');
  }
  if (!user) {
    logger.error('this provider should never render when user is missing');
  }
  if (isLoading) {
    logger.error('this provider should never render when isLoading is true');
  }

  useEffect(() => {
    logger.debug('UserStorageProvider effect is building a new service');

    if (!user || !isAuthenticated || isLoading) {
      logger.debug('skipping creating user storage');
      setUserStorage(null);
      return;
    }

    logger.debug('creating user storage');

    const service = new UserStorageService(user, logger);

    setUserStorage(service);
  }, [user, isAuthenticated, isLoading, logger]);

  return (
    <UserStorageContext.Provider value={{ service: userStorage }}>
      {!userStorage || !user || !isAuthenticated || isLoading ? <PageLoading details="recupero delle preferenze" /> : children}
    </UserStorageContext.Provider>
  );
};

// Custom hook to use the API client
export const useUserStorage = (): UserStorageService | null => {
  const context = useContext(UserStorageContext);
  if (!context) {
    throw new Error('useUserStorage must be used within an UserStorageProvider');
  }
  return context.service;
};

export const useMustHaveUserStorage = (): UserStorageService => {
  const context = useContext(UserStorageContext);
  if (!context) {
    throw new Error('useUserStorage must be used within an UserStorageProvider');
  }
  if (!context?.service) {
    throw new Error('useUserStorage must be used within an authenticated user context');
  }
  return context.service;
};
